(() => {
  'use strict';

  const root = document.getElementById('MyProductFloatingCart');
  const dataElement = document.getElementById('MyProductFloatingCartData');
  if (!root || !dataElement || root.dataset.initialized === 'true') return;

  let data;
  try {
    data = JSON.parse(dataElement.textContent || '{}');
  } catch (error) {
    console.error('[floating-cart] Product data could not be parsed.', error);
    return;
  }

  root.dataset.initialized = 'true';

  const variants = new Map(
    (data.variants || []).map((variant) => [String(variant.id), variant]),
  );
  const state = {
    variant: variants.get(String(data.initialVariantId)) || (data.variants || [])[0] || null,
    selectionState: 'available',
    selectedOptions: [],
    quantity: 1,
    submitting: false,
    feedbackTimer: null,
  };
  state.selectionState = state.variant?.available ? 'available' : 'soldout';

  const elements = {
    titles: root.querySelectorAll('[data-floating-title]'),
    prices: root.querySelectorAll('[data-floating-price]'),
    originalPrices: root.querySelectorAll('[data-floating-original-price]'),
    priceLabels: root.querySelectorAll('[data-floating-price-label]'),
    discounts: root.querySelectorAll('[data-floating-discount]'),
    variantImages: root.querySelectorAll('[data-floating-variant-image]'),
    deliveryWraps: root.querySelectorAll('[data-floating-delivery-wrap]'),
    deliveries: root.querySelectorAll('[data-floating-delivery]'),
    buttons: root.querySelectorAll('[data-floating-add-to-cart]'),
    buttonTexts: root.querySelectorAll('[data-floating-button-text]'),
    desktop: root.querySelector('[data-floating-cart-desktop]'),
    mobile: root.querySelector('[data-floating-cart-mobile]'),
  };

  const setText = (nodeList, value) => {
    nodeList.forEach((element) => {
      element.textContent = value;
    });
  };

  const setHidden = (nodeList, hidden) => {
    nodeList.forEach((element) => {
      element.hidden = hidden;
    });
  };

  const formatMoney = (cents) => {
    const amount = Number(cents) || 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: data.currency || 'USD',
      }).format(amount / 100);
    } catch (error) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  const formatDate = (date) => new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
  }).format(date);

  const getEstimatedDelivery = () => {
    const fastDate = new Date();
    const slowDate = new Date();
    fastDate.setDate(fastDate.getDate() + 3);
    slowDate.setDate(slowDate.getDate() + 9);
    return `${formatDate(fastDate)} – ${formatDate(slowDate)}`;
  };

  const getDiscount = (variant) => {
    const config = data.discountConfig?.[String(variant?.id)] || {};
    const discountText = String(config.disCountoff || '').trim();
    const discountValueMatch = discountText.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    const discountValue = discountValueMatch ? Number(discountValueMatch[0]) : NaN;
    const discountType = String(config.disCountType || '').trim().toLowerCase();
    const originalPrice = Number(variant?.price);
    let discountedPrice = null;

    if (Number.isFinite(originalPrice) && Number.isFinite(discountValue) && discountValue > 0) {
      if (discountType === 'off') {
        discountedPrice = originalPrice * (1 - (discountValue / 100));
      } else if (discountType === 'money') {
        discountedPrice = originalPrice - (discountValue * 100);
      }
    }

    if (discountedPrice !== null) {
      // 保留单件折后价的完整精度，乘以数量后再统一向上取整。
      discountedPrice = Math.max(0, discountedPrice);
    }

    const active = discountedPrice !== null && discountedPrice < originalPrice;
    return {
      config,
      active,
      discountedPrice: active ? discountedPrice : null,
      label: active
        ? `${discountText.replace(/\s*off\s*$/i, '').trim()}OFF`
        : '',
    };
  };

  const getPresaleText = (variantId) => {
    const config = data.presaleConfig || {};
    let text = '';
    if (config.isVariantConfig) {
      text = config.varPresaleDescObj?.[String(variantId)] || '';
    } else {
      text = config.prodPresaleDesc || '';
    }

    return String(text || '').trim();
  };

  const isPresale = (variant) => (
    Boolean(variant?.available) && Number(variant?.inventoryQuantity) < 1
  );

  const getDeliveryText = (variant) => {
    const isRegularSale = Boolean(variant?.available)
      && Number(variant?.inventoryQuantity) >= 1;

    if (isRegularSale) {
      return `Estimated delivery: ${getEstimatedDelivery()}`;
    }

    return getPresaleText(variant?.id);
  };

  const getDefaultButtonText = () => {
    if (state.selectionState === 'unavailable' || !state.variant) return 'Unavailable';
    if (!state.variant?.available) return 'Sold Out';
    const discount = getDiscount(state.variant);
    if (isPresale(state.variant) && !discount.active) return 'Preorder';
    return 'Add to Cart';
  };

  const render = () => {
    const isPurchasable = state.selectionState === 'available'
      && Boolean(state.variant?.available);

    if (!state.variant) {
      const selectedTitle = state.selectedOptions.filter(Boolean).join(' / ');
      setText(elements.titles, `${data.productTitle || ''}${selectedTitle ? ` - ${selectedTitle}` : ''}`);
      setText(elements.prices, '');
      setText(elements.originalPrices, '');
      setText(elements.discounts, '');
      setHidden(elements.originalPrices, true);
      setHidden(elements.priceLabels, true);
      setHidden(elements.discounts, true);
      setText(elements.deliveries, '');
      setHidden(elements.deliveryWraps, true);
      elements.variantImages.forEach((image) => {
        image.hidden = true;
      });
      if (!state.submitting) setText(elements.buttonTexts, getDefaultButtonText());
      elements.buttons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
      return;
    }

    const quantity = Math.max(1, Number(state.quantity) || 1);
    const discount = getDiscount(state.variant);
    const variantTitle = state.variant.title && state.variant.title !== 'Default Title'
      ? ` - ${state.variant.title}`
      : '';

    setText(elements.titles, `${data.productTitle || ''}${variantTitle}`);
    elements.variantImages.forEach((image) => {
      const imageUrl = String(state.variant.imageUrl || '');
      image.hidden = !imageUrl;
      if (imageUrl && image.src !== imageUrl) image.src = imageUrl;
      image.alt = `${data.productTitle || ''}${variantTitle}`;
    });
    const originalTotal = Number(state.variant.price) * quantity;
    // 总额向上取整到分，避免浮点运算尾差导致整分金额多加一分。
    const roundingTolerance = Number.EPSILON * Math.max(1, originalTotal) * 4;
    const displayedTotal = discount.active
      ? Math.max(0, Math.ceil(discount.discountedPrice * quantity - roundingTolerance))
      : originalTotal;

    setText(elements.prices, formatMoney(displayedTotal));
    setText(elements.originalPrices, formatMoney(originalTotal));
    setHidden(elements.originalPrices, !discount.active);
    setText(elements.priceLabels, 'Now');
    setHidden(elements.priceLabels, !discount.active);
    setText(elements.discounts, discount.label);
    setHidden(elements.discounts, !discount.active);

    const deliveryText = getDeliveryText(state.variant);
    setText(elements.deliveries, deliveryText);
    setHidden(elements.deliveryWraps, !deliveryText);

    if (!state.submitting) setText(elements.buttonTexts, getDefaultButtonText());
    elements.buttons.forEach((button) => {
      button.disabled = !isPurchasable || state.submitting;
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    });
  };

  const isVisible = (element) => Boolean(
    element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length)
  );

  const getVariantField = (form) => {
    if (!form) return null;
    return form.querySelector('#product-actual-select')
      || form.querySelector('select[name="id"]')
      || form.querySelector('input[name="id"]');
  };

  const getCandidateProductForms = () => {
    const originButtonArea = document.getElementById('shopifyOriginAddToCartFormId');
    const candidates = Array.from(document.querySelectorAll('#MainContent form[action*="/cart/add"]'));
    const originForm = originButtonArea?.closest('form');
    if (originForm && !candidates.includes(originForm)) candidates.unshift(originForm);

    return candidates.filter((form) => (
      !form.closest('.product-template-my-AddOn-wrap')
      && !form.closest('.my-product-floating-cart')
    ));
  };

  const findMainProductForm = () => {
    const currentId = String(state.variant?.id || '');
    const candidates = getCandidateProductForms();
    let bestForm = null;
    let bestScore = -Infinity;

    candidates.forEach((form) => {
      const variantField = getVariantField(form);
      const formVariantId = String(variantField?.value || '');
      let score = 0;
      if (formVariantId === currentId) score += 100;
      if (form.classList.contains('product-form')) score += 30;
      if (form.querySelector('#shopifyOriginAddToCartFormId')) score += 40;
      if (isVisible(form)) score += 20;
      if (score > bestScore) {
        bestScore = score;
        bestForm = form;
      }
    });

    return bestForm;
  };

  const findQuantityInput = () => {
    const mainForm = findMainProductForm();
    const preferred = document.getElementById('myObserveOriginProductQUantityId')
      || mainForm?.querySelector('input[name="quantity"]');
    if (preferred) return preferred;

    return Array.from(document.querySelectorAll('#MainContent input[name="quantity"]'))
      .find((input) => !input.closest('.product-template-my-AddOn-wrap')) || null;
  };

  const syncQuantity = (source) => {
    const mainForm = findMainProductForm();
    const originQuantityInput = document.getElementById('myObserveOriginProductQUantityId');
    const sourceIsMainQuantity = source?.matches?.('input[name="quantity"]')
      && !source.closest('.product-template-my-AddOn-wrap')
      && (source === originQuantityInput || source.closest('form') === mainForm);
    const quantityInput = sourceIsMainQuantity
      ? source
      : findQuantityInput();
    const value = Math.max(1, Number.parseInt(quantityInput?.value, 10) || 1);
    if (state.quantity !== value) {
      state.quantity = value;
      render();
    }
  };

  const setVariant = (variantId) => {
    const variant = variants.get(String(variantId || ''));
    if (!variant) return;
    state.variant = variant;
    state.selectionState = variant.available ? 'available' : 'soldout';
    state.selectedOptions = [];
    syncQuantity();
    render();
  };

  const setUnavailableSelection = (selectedOptions = []) => {
    state.variant = null;
    state.selectionState = 'unavailable';
    state.selectedOptions = Array.isArray(selectedOptions) ? selectedOptions : [];
    syncQuantity();
    render();
  };

  const getVariantIdFromForm = () => {
    const optionPickerId = window.optionPicker?.currentVariantId;
    if (optionPickerId && variants.has(String(optionPickerId))) return optionPickerId;

    const form = findMainProductForm();
    const fieldValue = getVariantField(form)?.value;
    if (fieldValue && variants.has(String(fieldValue))) return fieldValue;

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant && variants.has(String(urlVariant))) return urlVariant;
    return state.variant?.id;
  };

  const setSubmitting = (submitting, message) => {
    state.submitting = submitting;
    root.classList.toggle('is-submitting', submitting);
    if (message) setText(elements.buttonTexts, message);
    render();
  };

  const restoreButtonState = (delay = 1600) => {
    window.clearTimeout(state.feedbackTimer);
    state.feedbackTimer = window.setTimeout(() => {
      setSubmitting(false);
    }, delay);
  };

  const submitWithExistingForm = () => {
    const form = findMainProductForm();
    if (!form) return false;

    const variantField = getVariantField(form);
    if (variantField) {
      variantField.value = state.variant.id;
      variantField.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = state.quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const submitButton = form.querySelector('#shopifyOriginAddToCartFormId button[name="add"]')
      || form.querySelector('button[name="add"]')
      || form.querySelector('[data-pf-type="ProductATC"] button')
      || form.querySelector('button[type="submit"], input[type="submit"]');
    if (!submitButton || submitButton.disabled) return false;

    submitButton.click();
    return true;
  };

  const submitDirectly = async () => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: state.variant.id, quantity: state.quantity }],
      }),
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
  };

  const addToCart = async () => {
    if (
      state.submitting
      || state.selectionState !== 'available'
      || !state.variant?.available
    ) return;
    syncQuantity();
    setSubmitting(true, 'Adding...');

    if (submitWithExistingForm()) {
      restoreButtonState();
      return;
    }

    try {
      await submitDirectly();
      setText(elements.buttonTexts, 'Added');
    } catch (error) {
      console.error('[floating-cart] Product could not be added.', error);
      setText(elements.buttonTexts, 'Unable to add');
    }
    restoreButtonState(1800);
  };

  const updatePageOffset = () => {
    const activeBar = window.matchMedia('(max-width: 767px)').matches
      ? elements.mobile
      : elements.desktop;
    const height = Math.ceil(activeBar?.getBoundingClientRect().height || 0);
    document.documentElement.style.setProperty('--my-product-floating-cart-height', `${height}px`);
  };

  elements.buttons.forEach((button) => button.addEventListener('click', addToCart));

  document.addEventListener('myOptionChange', (event) => {
    const detail = event.detail || {};
    if (detail.selectionState === 'unavailable' || !detail.variantId) {
      setUnavailableSelection(detail.selectedOptions);
      return;
    }
    setVariant(detail.variantId);
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target?.matches?.('input[name="quantity"]')) {
      syncQuantity(target);
      return;
    }

    if (target?.matches?.('select[name="id"], input[name="id"]')) {
      setVariant(target.value);
      return;
    }

    window.setTimeout(() => setVariant(getVariantIdFromForm()), 0);
  });

  document.addEventListener('input', (event) => {
    if (!event.target?.matches?.('input[name="quantity"]')) return;
    syncQuantity(event.target);
    window.setTimeout(() => syncQuantity(event.target), 150);
  });

  document.addEventListener('click', (event) => {
    if (!event.target?.closest?.('#MainContent') || event.target.closest('.my-product-floating-cart')) return;
    [0, 120].forEach((delay) => {
      window.setTimeout(() => {
        syncQuantity();
        setVariant(getVariantIdFromForm());
      }, delay);
    });
  });

  ['variant:change', 'product:variant-change'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      setVariant(event.detail?.variant?.id || event.detail?.variantId || getVariantIdFromForm());
    });
  });

  window.addEventListener('popstate', () => setVariant(getVariantIdFromForm()));
  window.addEventListener('resize', updatePageOffset, { passive: true });

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(updatePageOffset);
    if (elements.desktop) resizeObserver.observe(elements.desktop);
    if (elements.mobile) resizeObserver.observe(elements.mobile);
  }

  document.documentElement.classList.add('my-product-floating-cart-active');
  setVariant(getVariantIdFromForm());
  syncQuantity();
  render();
  updatePageOffset();
})();
